import { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, Marker, Polyline, useJsApiLoader } from '@react-google-maps/api';
import { MapContainer, TileLayer, Marker as LMarker, Polyline as LPolyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Truck, MapPin, Navigation, RefreshCw, Phone, Gauge, Clock, Store, Flag } from 'lucide-react';
import { OrderAPI } from '../../lib/api';
import { GOOGLE_MAPS_API_KEY } from '../../lib/config';
import { Badge } from '../ui/badge';

interface LiveTrackingMapProps {
  orderId: string;
  active: boolean; // only poll while the tab/section is visible
}

type LatLngLit = { lat: number; lng: number };

type TrackData = {
  orderNumber?: string;
  status: string;
  isLive: boolean;
  driver: { name: string; phone: string } | null;
  driverLocation: { latitude: number; longitude: number; heading?: number | null; speed?: number | null; updatedAt?: string } | null;
  pickup: { name: string; address: string; latitude: number; longitude: number } | null;
  delivery: { name: string; address: string; latitude: number | null; longitude: number | null };
  route: { coordinates: [number, number][]; distance: number | null; duration?: number | null };
};

const ORANGE = '#F57C00';

// ---- shared helpers -------------------------------------------------------
const statusLabel = (s: string) =>
  (s || '').split('_').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');

const agoLabel = (s: number) =>
  s < 60 ? `${s}s ago` : s < 3600 ? `${Math.floor(s / 60)}m ago` : `${Math.floor(s / 3600)}h ago`;

function nearestIndex(route: [number, number][], p: [number, number]) {
  let best = 0, bestD = Infinity;
  for (let i = 0; i < route.length; i++) {
    const dLat = route[i][0] - p[0];
    const dLng = route[i][1] - p[1];
    const d = dLat * dLat + dLng * dLng;
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}

function distM(a: [number, number], b: [number, number]) {
  const r = 6371000;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLng = ((b[1] - a[1]) * Math.PI) / 180;
  const la1 = (a[0] * Math.PI) / 180, la2 = (b[0] * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * r * Math.asin(Math.min(1, Math.sqrt(h)));
}

// Top-down aerial truck as a rotated SVG data URL (sharp at any zoom).
function truckSvgUrl(heading: number) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 30 46">
    <g transform="rotate(${heading} 15 23)">
      <rect x="2" y="13" width="5.5" height="6.5" rx="1.5" fill="#1B1E22"/>
      <rect x="22.5" y="13" width="5.5" height="6.5" rx="1.5" fill="#1B1E22"/>
      <rect x="2" y="30" width="5.5" height="6.5" rx="1.5" fill="#1B1E22"/>
      <rect x="22.5" y="30" width="5.5" height="6.5" rx="1.5" fill="#1B1E22"/>
      <rect x="6" y="15.6" width="18" height="28.4" rx="2.8" fill="#EDEFF3" stroke="rgba(0,0,0,0.16)"/>
      <line x1="8" y1="22" x2="22" y2="22" stroke="rgba(0,0,0,0.08)"/>
      <line x1="8" y1="28" x2="22" y2="28" stroke="rgba(0,0,0,0.08)"/>
      <line x1="8" y1="34" x2="22" y2="34" stroke="rgba(0,0,0,0.08)"/>
      <rect x="4.5" y="6.5" width="3.4" height="1.8" rx="0.8" fill="#9A4E00"/>
      <rect x="22.1" y="6.5" width="3.4" height="1.8" rx="0.8" fill="#9A4E00"/>
      <rect x="6.5" y="2.5" width="17" height="12.5" rx="3" fill="${ORANGE}"/>
      <rect x="8.5" y="4" width="13" height="3.6" rx="1.4" fill="#BFE0F5"/>
    </g>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// ---- fit-bounds helpers ---------------------------------------------------
function LeafletFitOnce({ points }: { points: [number, number][] }) {
  const map = useMap();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || points.length === 0) return;
    done.current = true;
    if (points.length === 1) map.setView(points[0], 14);
    else map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 15 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

// Leaflet truck (rotated SVG), glides between positions.
function leafletTruckIcon(heading: number) {
  return L.divIcon({
    className: 'wk-truck',
    html: `<div style="width:40px;height:40px;filter:drop-shadow(0 1px 3px rgba(0,0,0,.4));"><img src="${truckSvgUrl(heading)}" width="40" height="40"/></div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}
function leafletPin(color: string) {
  return L.divIcon({
    className: 'wk-pin',
    html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 0 0 1px rgba(0,0,0,.25)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

// ---------------------------------------------------------------------------
export default function LiveTrackingMap({ orderId, active }: LiveTrackingMapProps) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'wk-gmaps',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const [data, setData] = useState<TrackData | null>(null);
  const [loading, setLoading] = useState(true);
  const [, force] = useState(0);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await OrderAPI.getLiveTracking(orderId);
        if (!cancelled && res.success) setData(res.data as TrackData);
      } catch {
        /* keep last known */
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const timer = setInterval(load, 8000);
    const tick = setInterval(() => force((n) => n + 1), 1000);
    return () => { cancelled = true; clearInterval(timer); clearInterval(tick); };
  }, [orderId, active]);

  const route: [number, number][] = useMemo(
    () => (data?.route?.coordinates ?? []).filter((c) => Array.isArray(c) && c.length === 2),
    [data]
  );

  const pickup = data?.pickup && data.pickup.latitude != null
    ? ([data.pickup.latitude, data.pickup.longitude] as [number, number]) : null;
  const delivery = data?.delivery && data.delivery.latitude != null
    ? ([data.delivery.latitude!, data.delivery.longitude!] as [number, number]) : null;
  const driver = data?.driverLocation && data.driverLocation.latitude != null
    ? ([data.driverLocation.latitude, data.driverLocation.longitude] as [number, number]) : null;
  const heading = data?.driverLocation?.heading ?? null;

  const points: [number, number][] = [...route];
  if (pickup) points.push(pickup);
  if (delivery) points.push(delivery);
  if (driver) points.push(driver);

  // Honest "last seen" from the driver's GPS fix.
  const fixMs = data?.driverLocation?.updatedAt ? Date.parse(data.driverLocation.updatedAt) : NaN;
  const secondsAgo = Number.isNaN(fixMs) ? null : Math.max(0, Math.round((Date.now() - fixMs) / 1000));
  const isFresh = secondsAgo != null && secondsAgo < 90;

  // Speed (km/h) from the GPS fix (m/s -> km/h).
  const rawSpeed = data?.driverLocation?.speed;
  const speedKmh = isFresh && rawSpeed != null && rawSpeed >= 0 ? rawSpeed * 3.6 : null;

  // Travelled vs remaining route split + remaining distance + live ETA.
  let doneRoute: [number, number][] = [];
  let remRoute: [number, number][] = route;
  let remainingM: number | null = null;
  if (driver && route.length >= 2) {
    const idx = nearestIndex(route, driver);
    doneRoute = route.slice(0, idx + 1);
    remRoute = route.slice(idx);
    remainingM = distM(driver, route[idx]);
    for (let i = idx; i < route.length - 1; i++) remainingM += distM(route[i], route[i + 1]);
  } else if (driver && delivery) {
    remainingM = distM(driver, delivery);
  }
  const distKm = remainingM != null ? remainingM / 1000
    : data?.route?.distance != null && data.route.distance >= 1000 ? data.route.distance / 1000 : null;
  const etaMin = remainingM != null && speedKmh != null && speedKmh > 4
    ? remainingM / (speedKmh * 1000 / 60)
    : data?.route?.duration ?? null;

  if (loading && !data) {
    return (
      <div className="h-[340px] rounded-lg border bg-muted/30 flex items-center justify-center text-muted-foreground">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Loading live tracking…
      </div>
    );
  }
  if (points.length === 0) {
    return (
      <div className="h-[200px] rounded-lg border bg-muted/30 flex flex-col items-center justify-center text-muted-foreground text-sm gap-2">
        <Navigation className="h-6 w-6 opacity-60" />
        No location or route data available for this order yet.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Status + live indicator */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="capitalize">{statusLabel(data?.status || '')}</Badge>
          {data?.isLive && driver && isFresh ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-600"></span>
              </span>
              Live
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">
              {driver && secondsAgo != null ? `Last seen ${agoLabel(secondsAgo)}`
                : data?.isLive ? 'Waiting for driver location…' : 'Not currently moving'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {data?.driver?.name && (
            <span className="flex items-center gap-1"><Truck className="h-3.5 w-3.5" />{data.driver.name}</span>
          )}
          {data?.driver?.phone && (
            <a href={`tel:${data.driver.phone}`} className="flex items-center gap-1 text-primary hover:underline">
              <Phone className="h-3.5 w-3.5" />{data.driver.phone}
            </a>
          )}
        </div>
      </div>

      {/* Map */}
      <div className="h-[360px] rounded-lg border overflow-hidden">
        {isLoaded ? (
          <GoogleTrackMap
            route={route} doneRoute={doneRoute} remRoute={remRoute}
            pickup={pickup} delivery={delivery} driver={driver}
            heading={isFresh ? heading : null}
          />
        ) : loadError ? (
          <LeafletTrackMap
            points={points} doneRoute={doneRoute} remRoute={remRoute}
            pickup={pickup} delivery={delivery} driver={driver}
            heading={isFresh ? heading : null}
          />
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Loading map…
          </div>
        )}
      </div>

      {/* Operational detail — admin sees everything at a glance */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Metric icon={<MapPin className="h-3.5 w-3.5" />} label="Distance left"
          value={distKm != null && distKm > 0 ? `${distKm.toFixed(1)} km` : '—'} />
        <Metric icon={<Clock className="h-3.5 w-3.5" />} label="ETA"
          value={etaMin != null && etaMin > 0 ? `~${Math.round(etaMin)} min` : '—'} />
        <Metric icon={<Gauge className="h-3.5 w-3.5" />} label="Speed"
          value={speedKmh != null ? `${Math.round(speedKmh)} km/h` : '—'} />
        <Metric icon={<Truck className="h-3.5 w-3.5" />} label="Order"
          value={data?.orderNumber ? `#${data.orderNumber}` : '—'} />
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <LegRow icon={<Store className="h-4 w-4 text-green-700" />} label="Pickup"
          name={data?.pickup?.name} address={data?.pickup?.address} />
        <LegRow icon={<Flag className="h-4 w-4 text-red-700" />} label="Drop"
          name={data?.delivery?.name} address={data?.delivery?.address} />
      </div>
    </div>
  );
}

function Metric({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/20 px-3 py-2">
      <div className="flex items-center gap-1 text-[11px] text-muted-foreground">{icon}{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}

function LegRow({ icon, label, name, address }: { icon: React.ReactNode; label: string; name?: string; address?: string }) {
  const detail = [name, address].filter(Boolean).join(' • ');
  return (
    <div className="rounded-md border bg-muted/20 px-3 py-2 flex items-start gap-2">
      <div className="mt-0.5">{icon}</div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold text-muted-foreground">{label}</div>
        <div className="text-xs truncate" title={detail}>{detail || '—'}</div>
      </div>
    </div>
  );
}

// ---- Google Maps renderer -------------------------------------------------
function GoogleTrackMap(props: {
  route: [number, number][]; doneRoute: [number, number][]; remRoute: [number, number][];
  pickup: [number, number] | null; delivery: [number, number] | null; driver: [number, number] | null;
  heading: number | null;
}) {
  const { doneRoute, remRoute, pickup, delivery, driver, heading } = props;
  const mapRef = useRef<google.maps.Map | null>(null);
  const fitted = useRef(false);
  const [pos, setPos] = useState<LatLngLit | null>(driver ? { lat: driver[0], lng: driver[1] } : null);
  const fromRef = useRef<LatLngLit | null>(pos);
  const raf = useRef<number>();

  const toLit = (p: [number, number]): LatLngLit => ({ lat: p[0], lng: p[1] });

  // Glide the truck to the new fix.
  useEffect(() => {
    if (!driver) return;
    const target = { lat: driver[0], lng: driver[1] };
    const from = fromRef.current ?? target;
    const start = performance.now();
    const dur = 1200;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / dur);
      const e = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      setPos({ lat: from.lat + (target.lat - from.lat) * e, lng: from.lng + (target.lng - from.lng) * e });
      if (t < 1) raf.current = requestAnimationFrame(tick);
      else fromRef.current = target;
    };
    raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver?.[0], driver?.[1]]);

  const fitOnce = (map: google.maps.Map) => {
    if (fitted.current) return;
    const pts = [...props.route, pickup, delivery, driver].filter(Boolean) as [number, number][];
    if (pts.length === 0) return;
    fitted.current = true;
    if (pts.length === 1) { map.setCenter(toLit(pts[0])); map.setZoom(14); return; }
    const b = new google.maps.LatLngBounds();
    pts.forEach((p) => b.extend(toLit(p)));
    map.fitBounds(b, 56);
  };

  const dotted: any = {
    strokeOpacity: 0,
    icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.6, strokeColor: ORANGE, scale: 3 }, offset: '0', repeat: '16px' }],
  };

  return (
    <GoogleMap
      mapContainerStyle={{ width: '100%', height: '100%' }}
      onLoad={(m) => { mapRef.current = m; fitOnce(m); }}
      options={{
        disableDefaultUI: true, zoomControl: true, gestureHandling: 'greedy', clickableIcons: false,
      }}
    >
      {doneRoute.length > 1 && (
        <Polyline path={doneRoute.map(toLit)} options={{ strokeColor: '#9AA3B2', strokeOpacity: 0.6, strokeWeight: 5 }} />
      )}
      {remRoute.length > 1 && (
        <Polyline path={remRoute.map(toLit)} options={{ strokeColor: ORANGE, strokeWeight: 5, ...dotted }} />
      )}
      {pickup && (
        <Marker position={toLit(pickup)} icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 7, fillColor: '#2E7D32', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 } as any} title="Pickup" />
      )}
      {delivery && (
        <Marker position={toLit(delivery)} icon={{ path: google.maps.SymbolPath.CIRCLE, scale: 7, fillColor: '#C62828', fillOpacity: 1, strokeColor: '#fff', strokeWeight: 2 } as any} title="Drop" />
      )}
      {pos && (
        <Marker
          position={pos}
          icon={{ url: truckSvgUrl(heading ?? 0), scaledSize: new google.maps.Size(40, 40), anchor: new google.maps.Point(20, 20) } as any}
          zIndex={1000}
        />
      )}
    </GoogleMap>
  );
}

// ---- Leaflet fallback renderer -------------------------------------------
function LeafletTrackMap(props: {
  points: [number, number][]; doneRoute: [number, number][]; remRoute: [number, number][];
  pickup: [number, number] | null; delivery: [number, number] | null; driver: [number, number] | null;
  heading: number | null;
}) {
  const { points, doneRoute, remRoute, pickup, delivery, driver, heading } = props;
  const center: [number, number] = points[0] ?? [20.59, 78.96];
  return (
    <MapContainer center={center} zoom={12} style={{ height: '100%', width: '100%' }} scrollWheelZoom>
      <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
      {doneRoute.length > 1 && (
        <LPolyline positions={doneRoute} pathOptions={{ color: '#9AA3B2', weight: 5, opacity: 0.6 }} />
      )}
      {remRoute.length > 1 && (
        <LPolyline positions={remRoute} pathOptions={{ color: ORANGE, weight: 5, opacity: 0.6, dashArray: '4 12', lineCap: 'round' }} />
      )}
      {pickup && <LMarker position={pickup} icon={leafletPin('#2E7D32')} />}
      {delivery && <LMarker position={delivery} icon={leafletPin('#C62828')} />}
      {driver && <LMarker position={driver} icon={leafletTruckIcon(heading ?? 0)} />}
      <LeafletFitOnce points={points} />
    </MapContainer>
  );
}
